import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — BÁO GIÁ ════════════════════════════════════════════════════════════════════
 * Nguồn sự thật DUY NHẤT cho hình dạng dữ liệu vào/ra của mọi cửa `/api/v1/az/quotes`.
 * Máy chủ xác thực bằng chính lược đồ này; trình duyệt lấy KIỂU từ chính lược đồ này.
 *
 * ⚠ TIỀN VÀO BẰNG CHUỖI. `sellPrice: 12345.67` (số thực) từng đi thẳng xuống CSDL vì generic
 *   TypeScript không kiểm gì lúc chạy. `MoneyIn` chặn ở biên và chuẩn hoá về 2 chữ số thập phân.
 */

export const QuoteStatus = z.enum(['draft', 'pending', 'approved', 'sent', 'won', 'lost'])
export type QuoteStatus = z.infer<typeof QuoteStatus>

/** Trường chung của tạo mới và sửa. Mọi trường tiền đi qua `MoneyIn`. */
const quoteFields = {
  customerAddrOverride: z.string().optional(),
  kind: z.enum(['outbound', 'inbound', 'internal']).optional(),
  pickupAddr: z.string().optional(),
  dropAddr: z.string().optional(),
  pickups: z.array(z.unknown()).optional(),
  drops: z.array(z.unknown()).optional(),
  cargo: z.string().optional(),
  weightTon: blankAsAbsent(z.coerce.number()),
  contSize: z.enum(['20', '40', '45']).optional(),
  contType: z.string().optional(),
  packId: z.string().optional(),
  templateId: z.string().optional(),
  revenues: z.array(z.unknown()).optional(),
  listPriceId: z.string().optional(),
  listPriceAt: blankAsAbsent(moneyField('Giá công bố')),
  costPriceAt: blankAsAbsent(moneyField('Giá vốn')),
  sellPrice: blankAsAbsent(moneyField('Giá chào khách')),
  estBuyPrice: blankAsAbsent(moneyField('Giá mua ước tính')),
  priceMode: z.enum(['list', 'cost']).optional(),
  markupPct: blankAsAbsent(z.coerce.number()),
  validUntil: blankAsAbsent(ISODate),
  paymentDays: blankAsAbsent(z.coerce.number().int()),
  terms: z.string().optional(),
  note: z.string().optional(),
  /** `null` (hoặc chuỗi rỗng) = GỠ link Drive — bản cũ `driveUrl: linkInput.trim() || undefined` (`Catalog.tsx:3185`). */
  driveUrl: z.string().nullable().optional(),
}

export const CreateQuoteBody = z.object({
  customerId: Uuid,
  /** ⧉ Nhân bản từ báo giá cũ — bản cũ giữ nguyên làm lịch sử, hai phiếu cùng ghi một dòng nhật ký. */
  copyFromId: blankAsAbsent(Uuid),
  ...quoteFields,
})

/**
 * ⚠ `customerId` là TUỲ CHỌN ở đây dù BẮT BUỘC lúc tạo. Sửa báo giá là gửi BẢN VÁ: màn hình chỉ
 *   gửi những ô người dùng vừa đổi. Bắt buộc lại mọi trường của lúc tạo là biến một bản vá thành
 *   một lần ghi đè toàn phần — ô nào màn quên gửi sẽ bị xoá trắng.
 */
export const UpdateQuoteBody = z.object({
  version: Version,
  customerId: blankAsAbsent(Uuid),
  ...quoteFields,
  /*
   * ⭐ `null` = XOÁ Ô (bao-gia-hh-4). Bản cũ `quotes.update(id, { ...core })` ghi cả trường `undefined` ⇒ ô xoá trắng mất thật
   * (`Az.tsx:4438-4455`). Vắng khoá vẫn là "không đổi" — đúng nghĩa bản vá ở trên.
   */
  customerAddrOverride: z.string().nullable().optional(),
  cargo: z.string().nullable().optional(),
  /** Khối lượng (tấn) — trước 22/10 máy chủ bỏ qua khoá này khi SỬA (lệch thấy ở Đợt 3); `null` = xoá ô. */
  weightTon: blankAsAbsent(z.coerce.number().nullable()),
  /** Cỡ cont suy từ số đầu của Loại cont (`Az.tsx:3967`); loại cont không bắt đầu bằng 20/40/45 thì `null` = xoá như bản cũ. */
  contSize: z.enum(['20', '40', '45']).nullable().optional(),
  contType: z.string().nullable().optional(),
  packId: z.string().nullable().optional(),
  listPriceId: z.string().nullable().optional(),
  listPriceAt: blankAsAbsent(moneyField('Giá công bố').nullable()),
  costPriceAt: blankAsAbsent(moneyField('Giá vốn').nullable()),
  estBuyPrice: blankAsAbsent(moneyField('Giá mua ước tính').nullable()),
  markupPct: blankAsAbsent(z.coerce.number().nullable()),
  validUntil: blankAsAbsent(ISODate.nullable()),
  paymentDays: blankAsAbsent(z.coerce.number().int().nullable()),
  terms: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
})

/** Bước quy trình chỉ cần `version` — mọi luật chuyển trạng thái nằm ở máy chủ. */
export const QuoteStepBody = z.object({ version: Version })
/**
 * ✉ Gửi khách. `to` = email người nhận; `via` chọn câu lịch sử như bản cũ: `quotes` (tab Báo giá, `Az.tsx:3409`) ⇒ "Gửi email báo
 * giá cho khách (<to>)"; `hub` (hub báo giá ở Danh mục, `Catalog.tsx:3227`, D02780) ⇒ "Gửi khách qua email: <to>". Vắng `to` = nút
 * "Gửi khách" thường.
 */
export const SendQuoteBody = QuoteStepBody.extend({ to: blankAsAbsent(z.string().max(320)), via: z.enum(['quotes', 'hub']).optional() })
export type SendQuoteBodyT = z.infer<typeof SendQuoteBody>
export const SubmitQuoteBody = z.object({
  version: Version,
  approverId: blankAsAbsent(Uuid.nullable()),
})
export const DecideQuoteBody = z.object({
  version: Version,
  approve: z.boolean({ required_error: 'Thiếu `approve`.' }),
  reason: z.string().optional(),
})
export const WinQuoteBody = z.object({ version: Version, jobId: z.string().optional() })

export const ListQuoteQuery = PageQuery.extend({
  customerId: blankAsAbsent(Uuid),
  status: QuoteStatus.optional(),
})

export const QuoteIdParams = z.object({ id: Uuid })

export type ListQuoteQueryT = z.infer<typeof ListQuoteQuery>

/* ══ BÁO GIÁ MẪU (`0066`) ════════════════════════════════════════════════════════════════════ */

/**
 * ⚠ `revenues` để `z.array(z.unknown())`, đúng như `az_quote.revenues`: khuôn dòng doanh thu do
 *   màn bán hàng chốt và đã có ca kiểm riêng ở đó. Khai lại một lược đồ thứ hai ở đây là hai khuôn
 *   cho cùng một danh sách, và chúng sẽ lệch nhau.
 */
export const CreateQuoteTemplateBody = z.object({
  name: z.string().min(1, 'Mẫu báo giá phải có tên.'),
  kind: z.enum(['outbound', 'inbound', 'internal']).default('outbound'),
  pickupAddr: blankAsAbsent(z.string()),
  dropAddr: blankAsAbsent(z.string()),
  revenues: z.array(z.unknown()).default([]),
  terms: blankAsAbsent(z.string()),
})

export const UpdateQuoteTemplateBody = z.object({
  version: Version,
  name: z.string().min(1, 'Mẫu báo giá phải có tên.').optional(),
  kind: z.enum(['outbound', 'inbound', 'internal']).optional(),
  pickupAddr: blankAsAbsent(z.string()),
  dropAddr: blankAsAbsent(z.string()),
  revenues: z.array(z.unknown()).optional(),
  terms: blankAsAbsent(z.string()),
  active: z.boolean().optional(),
})

export const DeleteQuoteTemplateBody = z.object({ version: Version })

/** Cửa đọc mẫu không có bộ lọc nào — bảng nhỏ, và ô chọn mẫu cần cả bảng cùng lúc. */
export const QuoteTemplateQuery = z.object({ activeOnly: blankAsAbsent(z.enum(['true', 'false'])) })

export type CreateQuoteTemplateBodyT = z.infer<typeof CreateQuoteTemplateBody>
export type UpdateQuoteTemplateBodyT = z.infer<typeof UpdateQuoteTemplateBody>
export type DeleteQuoteTemplateBodyT = z.infer<typeof DeleteQuoteTemplateBody>
export type QuoteTemplateQueryT = z.infer<typeof QuoteTemplateQuery>
